import {
	AutomationTriggerType,
	IAvailableAutomationTrigger,
	IResourceSelectorAutomationTrigger,
	KeyDateKind,
	ResourceSelectorId,
} from '@ViewModels';
import { getTriggerTypeLabel, RESOURCE_SELECTOR_DISPLAY } from '../../../models/utils/automationDisplayConfig';
import { getKeyDateKindFromResourceSelectorId } from '../../../models/utils/industryUtils';

/**
 * The API describes what starts an automation along two axes — `trigger._type` and, for
 * resource selector triggers, `trigger.resourceSelector`. Users think of it as one question,
 * and several kinds (New Policy, New Donation, New Matter, ...) exist on BOTH axes under the
 * same name, so a per-axis filter shows the same label twice with no way to tell them apart.
 * These options flatten both axes into one list, keeping every predicate a label can match.
 */
export interface ITriggerFilterOption {
	value: string;
	label: string;
	group: TriggerFilterGroup;
	triggerTypes: string[];
	resourceSelectors: string[];
	/** Manual automations are identified by the absence of a published trigger */
	matchesTemplatesWithoutTrigger?: boolean;
}

const TRIGGER_FILTER_GROUPS = ['New contacts', 'Key dates', 'New records', 'Activity', 'Other'] as const;

type TriggerFilterGroup = (typeof TRIGGER_FILTER_GROUPS)[number];

const OTHER_GROUP: TriggerFilterGroup = 'Other';
const KEY_DATES_GROUP: TriggerFilterGroup = 'Key dates';

const TRIGGER_TYPE_VALUE_PREFIX = 'type:';
const RESOURCE_SELECTOR_VALUE_PREFIX = 'selector:';

/** `Other` sorts last, so the earlier group is also the more specific one */
const mostSpecificGroup = (a: TriggerFilterGroup, b: TriggerFilterGroup): TriggerFilterGroup =>
	TRIGGER_FILTER_GROUPS.indexOf(a) <= TRIGGER_FILTER_GROUPS.indexOf(b) ? a : b;

const TRIGGER_TYPE_GROUPS: Partial<Record<AutomationTriggerType, TriggerFilterGroup>> = {
	[AutomationTriggerType.NewClient]: 'New contacts',
	[AutomationTriggerType.NewDonor]: 'New contacts',
	[AutomationTriggerType.NewLead]: 'New contacts',
	[AutomationTriggerType.NewPolicyTrigger]: 'New records',
	[AutomationTriggerType.NewDonationTrigger]: 'New records',
	[AutomationTriggerType.NewMatterTrigger]: 'New records',
	[AutomationTriggerType.OpportunityCreated]: 'New records',
	[AutomationTriggerType.MatterStageChangedTrigger]: 'Activity',
	[AutomationTriggerType.MatterStatusChangedTrigger]: 'Activity',
	[AutomationTriggerType.ServiceJobCompletedTrigger]: 'Activity',
	[AutomationTriggerType.EventRegistration]: 'Activity',
	[AutomationTriggerType.Meeting]: 'Activity',
	[AutomationTriggerType.Texting]: 'Activity',
};

/** Resource selectors that are not key dates and belong to a specific group */
const NON_KEY_DATE_RESOURCE_SELECTOR_GROUPS: Partial<Record<ResourceSelectorId, TriggerFilterGroup>> = {
	[ResourceSelectorId.NewPolicy]: 'New records',
	[ResourceSelectorId.NewDonations]: 'New records',
	[ResourceSelectorId.NewMatter]: 'New records',
	[ResourceSelectorId.MatterStageChanged]: 'Activity',
	[ResourceSelectorId.MatterStatusChanged]: 'Activity',
	[ResourceSelectorId.ServiceJobCompleted]: 'Activity',
};

/** Key dates that `getKeyDateKindFromResourceSelectorId` does not map */
const ADDITIONAL_KEY_DATE_SELECTORS = new Set<string>([
	ResourceSelectorId.GenericKeyDates,
	ResourceSelectorId.UpcomingPolicyRenewals,
]);

/**
 * Grouped by selector id, never by label: the configurable selectors (`TurningXX`..`TurningXX5`,
 * `Custom1`..`Custom10`) are named per account, so the text carries no meaning here — a custom key
 * date is one whatever the account called it. `getKeyDateKindFromResourceSelectorId` is the
 * codebase's record of which selectors are key dates, so unrecognized ones fall to `Other` rather
 * than being assumed date-driven.
 */
const resolveResourceSelectorGroup = (selectorId: ResourceSelectorId): TriggerFilterGroup => {
	const nonKeyDateGroup = NON_KEY_DATE_RESOURCE_SELECTOR_GROUPS[selectorId];
	if (nonKeyDateGroup) {
		return nonKeyDateGroup;
	}
	const keyDateKind = getKeyDateKindFromResourceSelectorId(selectorId);
	const isKeyDate =
		(!!keyDateKind && keyDateKind !== KeyDateKind.Unknown) || ADDITIONAL_KEY_DATE_SELECTORS.has(selectorId);
	return isKeyDate ? KEY_DATES_GROUP : OTHER_GROUP;
};

interface ITriggerFilterTemplate {
	publishedTrigger?: { _type?: string; resourceSelector?: string } | null;
}

const upsertOption = (
	optionsByLabel: Map<string, ITriggerFilterOption>,
	label: string,
	group: TriggerFilterGroup,
	patch: Partial<Pick<ITriggerFilterOption, 'triggerTypes' | 'resourceSelectors' | 'matchesTemplatesWithoutTrigger'>>
) => {
	const existing = optionsByLabel.get(label);
	if (existing) {
		for (const triggerType of patch.triggerTypes ?? []) {
			if (!existing.triggerTypes.includes(triggerType)) {
				existing.triggerTypes.push(triggerType);
			}
		}
		for (const resourceSelector of patch.resourceSelectors ?? []) {
			if (!existing.resourceSelectors.includes(resourceSelector)) {
				existing.resourceSelectors.push(resourceSelector);
			}
		}
		existing.matchesTemplatesWithoutTrigger =
			existing.matchesTemplatesWithoutTrigger || patch.matchesTemplatesWithoutTrigger;
		// Two selectors can share an account-supplied label; without this the group would
		// depend on the order the API listed them in
		existing.group = mostSpecificGroup(existing.group, group);
		return;
	}
	optionsByLabel.set(label, {
		value: '',
		label,
		group,
		triggerTypes: [...(patch.triggerTypes ?? [])],
		resourceSelectors: [...(patch.resourceSelectors ?? [])],
		matchesTemplatesWithoutTrigger: patch.matchesTemplatesWithoutTrigger,
	});
};

/** Prefers the trigger type so a merged option keeps one value regardless of API ordering */
const resolveOptionValue = (option: ITriggerFilterOption): string => {
	const [triggerType] = [...option.triggerTypes].sort();
	if (triggerType) {
		return `${TRIGGER_TYPE_VALUE_PREFIX}${triggerType}`;
	}
	const [resourceSelector] = [...option.resourceSelectors].sort();
	return `${RESOURCE_SELECTOR_VALUE_PREFIX}${resourceSelector}`;
};

/**
 * Rebuilds an option from the value alone, since a value carries its own predicate. Without this a
 * selection restored from the URL would filter nothing until the available-triggers query answered
 * — and nothing at all if the account no longer offers that trigger — while the dropdown read
 * "All Triggers", so the page silently disagreed with its own URL.
 */
const synthesizeOptionFromValue = (value: string): ITriggerFilterOption | null => {
	if (value.startsWith(TRIGGER_TYPE_VALUE_PREFIX)) {
		const triggerType = value.slice(TRIGGER_TYPE_VALUE_PREFIX.length) as AutomationTriggerType;
		if (!triggerType) {
			return null;
		}
		return {
			value,
			label: getTriggerTypeLabel(triggerType),
			group: TRIGGER_TYPE_GROUPS[triggerType] ?? OTHER_GROUP,
			triggerTypes: [triggerType as string],
			resourceSelectors: [],
			matchesTemplatesWithoutTrigger: triggerType === AutomationTriggerType.Manual,
		};
	}
	if (value.startsWith(RESOURCE_SELECTOR_VALUE_PREFIX)) {
		const selectorId = value.slice(RESOURCE_SELECTOR_VALUE_PREFIX.length) as ResourceSelectorId;
		if (!selectorId) {
			return null;
		}
		return {
			value,
			// Account-named selectors have no static label, so the id is the only name available here
			label: RESOURCE_SELECTOR_DISPLAY[selectorId]?.label ?? String(selectorId),
			group: resolveResourceSelectorGroup(selectorId),
			triggerTypes: [],
			resourceSelectors: [selectorId as string],
		};
	}
	return null;
};

export function resolveSelectedTriggerOptions(
	selectedValues: string[],
	options: ITriggerFilterOption[]
): ITriggerFilterOption[] {
	return selectedValues
		.map(value => options.find(option => option.value === value) ?? synthesizeOptionFromValue(value))
		.filter((option): option is ITriggerFilterOption => !!option);
}

export function deriveTriggerFilterOptions(availableTriggers: IAvailableAutomationTrigger[]): ITriggerFilterOption[] {
	const optionsByLabel = new Map<string, ITriggerFilterOption>();

	for (const item of availableTriggers) {
		const triggerType = item.trigger?._type as AutomationTriggerType | undefined;
		if (!triggerType) {
			continue;
		}
		if (triggerType === AutomationTriggerType.ResourceSelector) {
			const selectorId = (item.trigger as IResourceSelectorAutomationTrigger).resourceSelector;
			if (!selectorId) {
				continue;
			}
			const label = RESOURCE_SELECTOR_DISPLAY[selectorId]?.label ?? item.name ?? String(selectorId);
			upsertOption(optionsByLabel, label, resolveResourceSelectorGroup(selectorId), {
				resourceSelectors: [selectorId as string],
			});
			continue;
		}
		upsertOption(optionsByLabel, getTriggerTypeLabel(triggerType), TRIGGER_TYPE_GROUPS[triggerType] ?? OTHER_GROUP, {
			triggerTypes: [triggerType as string],
		});
	}

	// Manual automations have no published trigger, so the API never lists them
	upsertOption(optionsByLabel, getTriggerTypeLabel(AutomationTriggerType.Manual), OTHER_GROUP, {
		triggerTypes: [AutomationTriggerType.Manual as string],
		matchesTemplatesWithoutTrigger: true,
	});

	return [...optionsByLabel.values()]
		.map(option => ({ ...option, value: resolveOptionValue(option) }))
		.sort(
			(a, b) =>
				TRIGGER_FILTER_GROUPS.indexOf(a.group) - TRIGGER_FILTER_GROUPS.indexOf(b.group) ||
				a.label.localeCompare(b.label)
		);
}

export function groupTriggerFilterOptions(
	options: ITriggerFilterOption[]
): { group: TriggerFilterGroup; options: ITriggerFilterOption[] }[] {
	return TRIGGER_FILTER_GROUPS.map(group => ({
		group,
		options: options.filter(option => option.group === group),
	})).filter(entry => entry.options.length > 0);
}

export function templateMatchesTriggerFilter(
	template: ITriggerFilterTemplate,
	selectedOptions: ITriggerFilterOption[]
): boolean {
	if (selectedOptions.length === 0) {
		return true;
	}
	const triggerType = template.publishedTrigger?._type;
	const resourceSelector = template.publishedTrigger?.resourceSelector;
	return selectedOptions.some(option => {
		if (option.matchesTemplatesWithoutTrigger && !template.publishedTrigger) {
			return true;
		}
		if (triggerType && option.triggerTypes.includes(triggerType)) {
			return true;
		}
		return !!resourceSelector && option.resourceSelectors.includes(resourceSelector);
	});
}

export function countTemplatesByTriggerOption(
	templates: ITriggerFilterTemplate[],
	options: ITriggerFilterOption[]
): Record<string, number> {
	const counts: Record<string, number> = {};
	for (const option of options) {
		counts[option.value] = templates.filter(template => templateMatchesTriggerFilter(template, [option])).length;
	}
	return counts;
}
